import { useState, useEffect, useCallback, useRef } from 'react';
import ledgerApi from '../lib/ledgerApi';

const CACHE_TTL = 120000; // 2 min

export default function useAlerts(user, businesses) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastFetch = useRef(0);

  const linkedBusinesses = (businesses || []).filter(
    (b) => b.roles?.[user?.uid] && b.ledgerEntityId
  );

  const refresh = useCallback(async (force = false) => {
    if (!user || !businesses?.length) return;
    if (!force && Date.now() - lastFetch.current < CACHE_TTL) return;

    setLoading(true);
    const all = [];

    await Promise.all(
      linkedBusinesses.map(async (b) => {
        const eid = b.ledgerEntityId;
        const name = b.name;

        // Bank summary — unmatched items
        try {
          const summary = await ledgerApi.bankSummary(eid);
          const pending = summary.reduce((s, a) => s + (a.pending_count || 0), 0);
          if (pending > 0) {
            all.push({
              id: `bank-${eid}`,
              type: 'bank_unmatched',
              severity: pending > 20 ? 'high' : 'medium',
              category: 'Financial',
              entity: name,
              entityId: eid,
              title: `${pending} unmatched bank item${pending > 1 ? 's' : ''}`,
              action: ledgerApi.deepLink(eid, 'bank'),
              count: pending,
            });
          }
        } catch {}

        // Snapshot — health, runway, breakeven
        try {
          const snap = await ledgerApi.snapshot(eid);
          if (snap.business_health != null && snap.business_health < 50) {
            all.push({
              id: `health-${eid}`,
              type: 'health_low',
              severity: snap.business_health < 30 ? 'high' : 'medium',
              category: 'Business Health',
              entity: name,
              entityId: eid,
              title: `Health score ${snap.business_health}/100`,
              action: ledgerApi.deepLink(eid, 'dashboard'),
            });
          }
          if (snap.cash_runway_months != null && snap.cash_runway_months < 3) {
            all.push({
              id: `runway-${eid}`,
              type: 'runway_low',
              severity: snap.cash_runway_months < 1.5 ? 'high' : 'medium',
              category: 'Financial',
              entity: name,
              entityId: eid,
              title: `Cash runway: ${snap.cash_runway_months.toFixed(1)} months`,
              action: ledgerApi.deepLink(eid, 'dashboard'),
            });
          }
          if (snap.breakeven_state === 'Below' && snap.month?.revenue > 0) {
            all.push({
              id: `breakeven-${eid}`,
              type: 'below_breakeven',
              severity: 'medium',
              category: 'Financial',
              entity: name,
              entityId: eid,
              title: 'Operating below breakeven',
              action: ledgerApi.deepLink(eid, 'pnl'),
            });
          }
          if (snap.revenue_trend_pct_mom != null && snap.revenue_trend_pct_mom < -5) {
            all.push({
              id: `trend-${eid}`,
              type: 'revenue_declining',
              severity: snap.revenue_trend_pct_mom < -15 ? 'high' : 'low',
              category: 'Business Health',
              entity: name,
              entityId: eid,
              title: `Revenue trending ${snap.revenue_trend_pct_mom.toFixed(1)}%/mo`,
              action: ledgerApi.deepLink(eid, 'pnl'),
            });
          }
          // Pending reconciliation
          if (snap.pending_reconciliation > 100) {
            all.push({
              id: `recon-${eid}`,
              type: 'recon_backlog',
              severity: snap.pending_reconciliation > 300 ? 'high' : 'medium',
              category: 'Financial',
              entity: name,
              entityId: eid,
              title: `${snap.pending_reconciliation} transactions pending reconciliation`,
              action: ledgerApi.deepLink(eid, 'transactions'),
            });
          }
        } catch {}
      })
    );

    // Pending invites (Firestore)
    try {
      const { db } = await import('../firebase/config');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const owned = businesses.filter((b) => b.roles?.[user.uid] === 'owner');
      let totalInvites = 0;
      for (const b of owned) {
        const q = query(
          collection(db, 'pendingInvites'),
          where('businessId', '==', b.id),
          where('claimed', '==', false),
        );
        const snap = await getDocs(q);
        totalInvites += snap.size;
      }
      if (totalInvites > 0) {
        all.push({
          id: 'invites-pending',
          type: 'pending_invites',
          severity: 'low',
          category: 'Access',
          entity: 'All',
          title: `${totalInvites} pending invite${totalInvites > 1 ? 's' : ''} unclaimed`,
          action: '/access',
          isInternal: true,
        });
      }
    } catch {}

    // Newsletter — check if previous month is available
    const now = new Date();
    const pm = now.getMonth() === 0 ? 12 : now.getMonth();
    const py = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const prevMonth = `${py}-${String(pm).padStart(2, '0')}`;
    const hasKng = linkedBusinesses.some((b) => b.ledgerEntityId === 'kng');
    if (hasKng) {
      all.push({
        id: 'newsletter-kng',
        type: 'newsletter',
        severity: 'info',
        category: 'Reports',
        entity: 'KNG',
        entityId: 'kng',
        title: `Generate ${prevMonth} newsletter`,
        action: ledgerApi.deepLink('kng', 'newsletter'),
      });
    }

    // Sort: high > medium > low > info
    const sevOrder = { high: 0, medium: 1, low: 2, info: 3 };
    all.sort((a, b) => (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3));

    setAlerts(all);
    setLoading(false);
    lastFetch.current = Date.now();
  }, [user, businesses, linkedBusinesses]);

  useEffect(() => { refresh(); }, [refresh]);

  return { alerts, loading, refresh, count: alerts.length };
}
